import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { BsArrowLeft } from "react-icons/bs";
import { ReactComponent as Cancel } from "../../../../assets/svgs/cancel.svg";
import {
  ImageLoaderUpdate,
  ModalCompAdvanceUpdate,
  ConfirmationModal,
  ButtonComp,
} from "../../../../components";
import CopyComp from "../../../../components/Common/CopyComp";
import bgImg from "../../../../assets/images/bg-img.webp";
import { ReactComponent as Edit } from "../../../../assets/svgs/edit-icon.svg";
import { ReactComponent as Delete } from "../../../../assets/svgs/delete.svg";
import CustomSelect from "../../../../components/custom-select/custom-select";
import { customStyles } from "../../../../components/custom-select/custom-styles";
import { useGetBranchesQuery } from "../../../../store/admin/settings/branchSlice";
import {
  useAssignBranchMutation,
  useRemoveBranchFromStaffMutation,
  useUpdateStaffMutation,
} from "../../../../store/admin/staff";
import toast from "react-hot-toast";
import axios from "axios";
import moment from "moment";

const formatLastLogin = (value) => {
  if (!value) return "Never";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY HH:mm") : "—";
};

const closeEditModal = ({
  setModalName,
  setImgFile,
  setErrorImg,
  imgPreview,
  setImgPreview,
}) => {
  setModalName();
  setImgFile(null);
  setErrorImg(null);
  if (imgPreview) {
    URL.revokeObjectURL(imgPreview);
    setImgPreview(null);
  }
};
export default function SingleStaff({
  // onNext,
  staffDetails,
  refetch,
}) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [getModalName, setModalName] = useState();
  const { isLoading, data } = useGetBranchesQuery();
  const [newBranch, setNewBranch] = useState([]);
  const [imgFile, setImgFile] = useState();
  const [errorImg, setErrorImg] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [profilePictureKey, setProfilePictureKey] = useState(0);

  // State for confirmation modal
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    branchToDelete: null,
  });
  const options =
    data?.map((option) => ({
      value: option.id,
      label: option.name,
    })) || [];

  // useAddBranchMutation
  const [assignBranch, { isLoading: addingBranch }] = useAssignBranchMutation();
  const [removeBranchFromStaff, { isLoading: isRemovingBranch }] =
    useRemoveBranchFromStaffMutation();
  const [updateStaff, { isLoading: isUpdatingStaff }] =
    useUpdateStaffMutation();

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (imgPreview) {
        URL.revokeObjectURL(imgPreview);
      }
    };
  }, [imgPreview]);

  // Image upload handler
  const updateImgFile = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setImgFile(null);
      setImgPreview(null);
      return setErrorImg("Upload an image");
    }
    const size = file?.size / 1024 / 1024;
    const type = file?.type?.split("/")[1];
    const validTypes = ["jpeg", "jpg", "png"];
    if (size > 10 || !validTypes.includes(type)) {
      setErrorImg(
        "Invalid. File size must not be more than 10mb\nFile must be either jpg jpeg png"
      );
      setImgFile(null);
      setImgPreview(null);
    } else {
      setErrorImg(null);
      setImgFile(file);
      setImgPreview(URL.createObjectURL(file));
    }
  };

  // Update only profile picture handler
  const handleUpdateProfilePicture = async () => {
    if (!imgFile || errorImg) {
      return;
    }

    try {
      const toastId = toast.loading("Updating profile picture...");

      // Upload image to Cloudinary
      const cloudName =
        process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "afrilocales";
      const uploadPreset =
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "svmprhvf";
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;

      const photoData = new FormData();
      photoData.append("file", imgFile);
      photoData.append("upload_preset", uploadPreset);

      const photoUrl = await axios
        .post(uploadUrl, photoData)
        .then((response) => response?.data?.secure_url)
        .catch(() => {
          toast.error("Failed to upload image", { id: toastId });
          return null;
        });

      if (!photoUrl) return;

      // Add cache-busting parameter to the photo URL
      const photoUrlWithCacheBust = photoUrl.includes("?")
        ? `${photoUrl}&t=${Date.now()}`
        : `${photoUrl}?t=${Date.now()}`;

      // Update only the photo field with existing staff data
      const payload = {
        first_name: staffDetails?.first_name,
        last_name: staffDetails?.last_name,
        email: staffDetails?.email,
        phone: staffDetails?.phone,
        dob: staffDetails?.dob || null,
        role_id: staffDetails?.role_id,
        address: staffDetails?.address,
        country_id: staffDetails?.country_id,
        branch_id: staffDetails?.active_branch_id || staffDetails?.branch_id,
        photo: photoUrlWithCacheBust,
      };

      const response = await updateStaff({
        staffId: id,
        payload: payload,
      });

      // Check if the response indicates an error
      if ("error" in response) {
        const { data } = response?.error;
        toast.error(data?.message || "An error occurred", { id: toastId });
        return;
      }

      // Check if the response data indicates an error
      if (response?.data?.status === false) {
        toast.error(response?.data?.message || "An error occurred", {
          id: toastId,
        });
        return;
      }

      toast.success("Profile picture updated successfully", { id: toastId });

      // Force profile picture refresh by updating the key
      setProfilePictureKey((prev) => prev + 1);

      setModalName(); // Close modal
      setImgFile(null);
      setErrorImg(null);
      if (imgPreview) {
        URL.revokeObjectURL(imgPreview);
        setImgPreview(null);
      }

      // Refetch the staff data to show the updated profile picture
      if (refetch) {
        refetch();
      }
    } catch (error) {
      toast.error("An error occurred while updating profile picture");
    }
  };
  const handleSubmit = async () => {
    const updatedValues = {
      payload: {
        branches: newBranch,
      },
      staffId: id,
    };

    const response = await assignBranch(updatedValues);
    if ("error" in response) {
      const { data } = response?.error;
      toast.error(data.message);
      return;
    }
    const { message } = response?.data;
    toast.success(message);

    // Auto-refresh staff data
    if (refetch) {
      refetch();
    }

    setNewBranch([]);
    setModalName();
  };
  // Handle branch deletion confirmation
  const handleDeleteBranchClick = (branchId) => {
    // Find the branch details
    const branchToDelete = staffDetails?.branches?.find(
      (item) => item.branch_id === branchId
    );

    if (!branchToDelete) return;

    // Don't allow deleting the current active branch
    if (branchId === staffDetails?.active_branch_id) {
      toast.error(
        "Cannot delete the currently active branch. Please switch to another branch first."
      );
      return;
    }

    setConfirmModal({
      isOpen: true,
      branchToDelete: branchToDelete,
    });
  };

  // Handle confirmed branch deletion
  const handleConfirmDeleteBranch = async () => {
    if (!confirmModal.branchToDelete) return;

    try {
      const toastId = toast.loading("Removing branch...");

      const branchId = confirmModal.branchToDelete.branch_id;

      const response = await removeBranchFromStaff({
        staffId: id,
        branchId: branchId,
      });

      if ("error" in response) {
        const { data } = response?.error;
        toast.error(data.message, { id: toastId });
        return;
      }
      const { message } = response?.data;
      toast.success(message, { id: toastId });

      // Auto-refresh staff data
      if (refetch) {
        refetch();
      }

      // Close modal
      setConfirmModal({
        isOpen: false,
        branchToDelete: null,
      });

      return;
    } catch (error) {
      toast.error("An error occurred while removing branch");
    } finally {
      setConfirmModal({
        isOpen: false,
        branchToDelete: null,
      });
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setConfirmModal({
      isOpen: false,
      branchToDelete: null,
    });
  };
  const useModal = [
    {
      name: "Add New Branch",
      component: (
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <div className="relative border-b border-gray-200 pb-4 mb-6">
            <button
              onClick={() => setModalName()}
              className="absolute left-0 top-0 p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <Cancel className="w-5 h-5 text-gray-500" />
            </button>
            <h3 className="text-xl font-bold text-gray-800 text-center">
              Add New Branch
            </h3>
            <p className="text-gray-500 text-center mt-1 text-sm">
              Assign branches to this staff member
            </p>
          </div>

          {/* Form Content */}
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-blue-500 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-blue-700 text-sm font-medium">
                  You can add multiple branches at once
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Branches
              </label>
              <CustomSelect
                options={options}
                styles={customStyles}
                isSearchable={false}
                label={"Branch"}
                isLoading={isLoading}
                onChange={(selectedOptions) =>
                  setNewBranch(
                    selectedOptions.map((option) => ({
                      branch_id: option.value,
                    }))
                  )
                }
                isMulti
                placeholder="Choose branches to assign"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <ButtonComp
                btnText="Cancel"
                btnClassName="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium border-2 border-gray-300 hover:border-gray-400"
                onClick={() => setModalName()}
              />
              <ButtonComp
                btnText={addingBranch ? "Adding..." : "Add Branches"}
                btnClassName={`flex-1 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                  addingBranch
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed border-2 border-gray-300"
                    : "bg-[var(--color-primary)] text-white hover:opacity-90 shadow-md hover:shadow-lg border-2 border-transparent"
                }`}
                onClick={handleSubmit}
                disabled={addingBranch}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Edit Profile Picture",
      component: (
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <div className="relative border-b border-gray-200 pb-4 mb-6">
            <button
              onClick={() => {
                setModalName();
                setImgFile(null);
                setErrorImg(null);
                if (imgPreview) {
                  URL.revokeObjectURL(imgPreview);
                  setImgPreview(null);
                }
              }}
              className="absolute left-0 top-0 p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <Cancel className="w-5 h-5 text-gray-500" />
            </button>
            <h3 className="text-xl font-bold text-gray-800 text-center">
              Change Profile Picture
            </h3>
          </div>

          {/* Profile Picture Preview */}
          <div className="flex flex-col items-center space-y-6">
            <div className="relative">
              <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-white shadow-lg ring-4 ring-gray-100">
                <ImageLoaderUpdate
                  key={`change-${profilePictureKey}-${
                    imgPreview || staffDetails?.photo
                  }`}
                  src={imgPreview || staffDetails?.photo}
                  width="160"
                  height="160"
                  imgClass="rounded-full w-full h-full object-cover"
                />
              </div>
              {imgFile && (
                <div className="absolute -bottom-2 -right-2 bg-green-500 text-white rounded-full p-2 shadow-lg">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>

            {/* File Upload */}
            <div className="w-full">
              <input
                type="file"
                accept="image/*"
                onChange={updateImgFile}
                className="hidden"
                id="profile-picture-only-upload"
              />
              <label
                htmlFor="profile-picture-only-upload"
                className="flex flex-col items-center w-full px-6 py-4 bg-[var(--color-secondary)] text-white rounded-xl cursor-pointer hover:bg-[#255a6b] transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <svg
                  className="w-6 h-6 mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="font-semibold">
                  {imgFile ? "Choose Different Photo" : "Choose New Photo"}
                </span>
                <span className="text-blue-100 text-sm mt-1">
                  JPG, PNG up to 10MB
                </span>
              </label>
            </div>

            {/* Error Message */}
            {errorImg && (
              <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-500 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-red-700 text-sm">{errorImg}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 w-full">
              <ButtonComp
                type="button"
                btnText="Cancel"
                BorderColor="#a1a0a0"
                btnClassName="flex-1 px-4 py-3 rounded-xl text-[#383838] font-medium"
                ButtonInlineStyle={{ backgroundColor: "#ffffff" }}
                onClick={() =>
                  closeEditModal({
                    setModalName,
                    setImgFile,
                    setErrorImg,
                    imgPreview,
                    setImgPreview,
                  })
                }
              />
              <ButtonComp
                type="button"
                btnText={isUpdatingStaff ? "Updating..." : "Update Photo"}
                loading={isUpdatingStaff}
                disabled={!imgFile || errorImg || isUpdatingStaff}
                btnClassName="flex-1 px-4 py-3 rounded-xl text-white font-medium"
                ButtonInlineStyle={{
                  backgroundColor:
                    !imgFile || errorImg || isUpdatingStaff
                      ? "#d1d5db"
                      : "var(--color-primary)",
                  border: "none",
                  color:
                    !imgFile || errorImg || isUpdatingStaff
                      ? "#6b7280"
                      : "#ffffff",
                }}
                onClick={handleUpdateProfilePicture}
              />
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section className="w-full flex flex-col gap-4">
      <div className="w-full h-[82px] flex gap-2 items-center pl-10 bg-white rounded-[10px]">
        <BsArrowLeft
          size={30}
          onClick={() => navigate(-1)}
          className="me-3 pointer"
        />
        <h4 className=" text-2xl font-bold text-[#45464E]">Profile details</h4>
      </div>
      <div className="w-full flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-[60%] bg-white rounded-3xl px-6 py-4">
          <div className="w-full relative border-b pb-4">
            <div className="w-full h-[180px] rounded-b-[50px] relative">
              <ImageLoaderUpdate
                src={bgImg}
                imageWrapperClassName="w-full h-full"
                imgClass="w-full h-full rounded-b-[50px] "
              />
            </div>
            <div className="w-full flex flex-col items-center -mt-[60px]">
              <div className="h-[150px] w-[150px] rounded-full relative overflow-hidden border-4 border-white shadow-lg">
                <ImageLoaderUpdate
                  key={`profile-${profilePictureKey}-${staffDetails?.photo}`}
                  src={staffDetails?.photo}
                  width="150"
                  height="150"
                  imgClass="rounded-full w-full h-full object-cover"
                />
                <div
                  className="absolute -right-4 h-10 w-10 rounded-full bg-white top-[50%] grid place-content-center cursor-pointer shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-blue-50 border-2 border-gray-100"
                  onClick={() => setModalName("Edit Profile Picture")}
                  title="Change Profile Picture"
                >
                  <Edit className="w-4 h-4 text-gray-600 hover:text-blue-600 transition-colors duration-200" />
                </div>
              </div>
              <p className="text-[#1E1E1E] font-semibold">{`${staffDetails?.first_name}   ${staffDetails?.last_name}`}</p>
              <p className="text-[#858585] text-sm">
                Staff ID: {staffDetails?.id}
              </p>
            </div>
          </div>
          <div className="py-4 flex flex-col gap-4 border-b">
            <p className="text-[#1E1E1E] font-semibold">Personal Information</p>
            <div>
              <p className="text-[#858585] text-sm">Date of Birth</p>
              <div className="flex justify-between w-full">
                <p className="text-[#1E1E1E] font-semibold">
                  {staffDetails?.dob
                    ? new Date(staffDetails.dob).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "Not set"}
                </p>
              </div>
            </div>
            <div>
              <p className="text-[#858585] text-sm">Role</p>
              <div className="flex justify-between w-full">
                <p className="text-[#1E1E1E] font-semibold">
                  {staffDetails?.role?.name || "Not assigned"}
                </p>
              </div>
            </div>
            <div>
              <p className="text-[#858585] text-sm">Last login</p>
              <div className="flex justify-between w-full">
                <p className="text-[#1E1E1E] font-semibold">
                  {formatLastLogin(staffDetails?.last_login_at)}
                </p>
              </div>
            </div>
          </div>
          <div className="py-4 flex flex-col gap-4 border-b">
            <p className="text-[#1E1E1E] font-semibold">Contact Information</p>
            <div>
              <p className="text-[#858585] text-sm">Email address</p>

              <div className="flex justify-between w-full">
                <p className="text-[#1E1E1E] font-semibold">
                  {staffDetails?.email}
                </p>
                <CopyComp text={staffDetails?.email} iconClassName="ms-2" />
              </div>
            </div>
            <div>
              <p className="text-[#858585] text-sm">Phone number</p>
              <div className="flex justify-between w-full">
                <p className="text-[#1E1E1E] font-semibold">
                  {staffDetails?.phone}
                </p>
                <CopyComp text={staffDetails?.phone} iconClassName="ms-2" />
              </div>
            </div>
          </div>
          <div className="w-full py-4 flex flex-col gap-4">
            <h4 className=" text-base font-semibold text-[#1E1E1E]">
              Address Information
            </h4>
            <div className="">
              <p className="text-[#1E1E1E] font-semibold">Full address</p>
              <p className="text-[#858585] text-sm">{staffDetails?.address}</p>
            </div>
            <div className="flex justify-between flex-wrap">
              <div className="">
                <p className="text-[#1E1E1E] font-semibold">Country</p>
                <p className="text-[#858585] text-sm">
                  {staffDetails?.country?.name}
                </p>
              </div>
              <div className="">
                <p className="text-[#1E1E1E] font-semibold">State</p>
                <p className="text-[#858585] text-sm">
                  {staffDetails?.city?.name}
                </p>
              </div>
              <div className="">
                <p className="text-[#1E1E1E] font-semibold">Landmark</p>
                <p className="text-[#858585] text-sm">
                  {staffDetails?.city?.landmark || "Nil"}
                </p>
              </div>
            </div>
          </div>
          <div className="py-4 flex flex-col gap-4 border-b">
            <h4 className="text-base font-semibold text-[#1E1E1E]">
              Account Information
            </h4>
            <div className="flex justify-between flex-wrap">
              <div className="">
                <p className="text-[#1E1E1E] font-semibold">Status</p>
                <p className="text-[#858585] text-sm">
                  {staffDetails?.is_suspended ? (
                    <span className="text-red-600 font-semibold">
                      Suspended
                    </span>
                  ) : (
                    <span className="text-green-600 font-semibold">Active</span>
                  )}
                </p>
              </div>
              <div className="">
                <p className="text-[#1E1E1E] font-semibold">Staff Since</p>
                <p className="text-[#858585] text-sm">
                  {staffDetails?.created_at
                    ? new Date(staffDetails.created_at).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )
                    : "Unknown"}
                </p>
              </div>
            </div>
          </div>
          <div className="py-4 flex w-full">
            <button
              className="flex items-center px-6 py-3 bg-[var(--color-primary)] text-white rounded-xl hover:opacity-90 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
              onClick={() =>
                navigate(
                  `/super_admin/staff_registration/view_staff/${id}/edit`
                )
              }
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Edit Profile
            </button>
          </div>
        </div>

        <div className="w-full lg:w-[40%] bg-white rounded-[20px] p-4 lg:p-6">
          {/* Staff Summary Information */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Staff Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-sm text-gray-600">Active Branch</p>
                <p className="font-semibold text-gray-800">
                  {staffDetails?.active_branch?.name || "Not assigned"}
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-sm text-gray-600">Total Branches</p>
                <p className="font-semibold text-gray-800">
                  {staffDetails?.branches?.length || 0}
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-sm text-gray-600">Company</p>
                <p className="font-semibold text-gray-800">
                  {staffDetails?.company?.name || "Unknown"}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between border-b pb-4">
            <p className="text-text-primary font-bold text-2xl">Branches</p>
            <button
              className="flex items-center px-6 py-3 bg-[var(--color-primary)] text-white rounded-xl hover:opacity-90 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
              onClick={() => setModalName("Add New Branch")}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add New Branch
            </button>
          </div>
          <div className="py-4 grid grid-cols-1 gap-y-5">
            {staffDetails?.branches.map((item, i) => (
              <div
                className="w-full rounded-[20px] bg-white"
                style={{
                  boxShadow:
                    "2px 2px 10px 0px rgba(0, 0, 0, 0.05), -2px -2px 10px 0px rgba(0, 0, 0, 0.05)",
                }}
                key={i}
              >
                <div className="w-full flex justify-center items-center bg-[#FAFAFA] py-4">
                  <p className="text-sm text-text-primary font-bold">
                    {item?.branch?.name}
                  </p>
                </div>
                <div className="w-full p-4 flex flex-col gap-4">
                  <div className="w-full relative flex justify-center items-center">
                    <div className="py-2 px-3 rounded-[30px] bg-[var(--color-secondary-soft)]">
                      <p className="text-[var(--color-secondary)] text-sm font-semibold">
                        {item?.branch?.name}
                      </p>
                    </div>
                    <button
                      className="absolute right-0 h-8 w-8 rounded-full bg-[#FAFAFA] hover:bg-red-50 grid place-content-center transition-colors duration-200 cursor-pointer border border-gray-200 hover:border-red-300"
                      onClick={() => handleDeleteBranchClick(item.branch_id)}
                      title="Remove branch"
                    >
                      <Delete className="w-4 h-4 text-gray-600 hover:text-[#FF4343] transition-colors duration-200" />
                    </button>
                  </div>

                  <div className="w-full text-center">
                    <p className="text-[#383838] font-semibold text-sm">
                      Address
                    </p>
                    <p className="text-text-secondary text-xs">
                      {item?.branch?.address}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ModalCompAdvanceUpdate
        centered
        arrayComp={useModal}
        pageName={getModalName}
        handleClose={() => setModalName()}
      />

      {/* Confirmation Modal for Branch Deletion */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDeleteBranch}
        title="Remove Branch"
        message={`Are you sure you want to remove "${confirmModal.branchToDelete?.branch?.name}" from this staff's assigned branches? This action cannot be undone.`}
        confirmText="Yes, Remove Branch"
        cancelText="Cancel"
        confirmButtonClass="bg-danger text-white"
        cancelButtonClass="bg-secondary text-white"
        isLoading={isRemovingBranch}
      />
    </section>
  );
}
